import React, { useState, useEffect } from 'react';
import { useProducts } from '../context/ProductContext';
import { useQuote } from '../context/QuoteContext';
import { SafeImage } from '../components/SafeImage';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { formatKsh } from '../utils/formatters';
import {
  ArrowLeft,
  ShoppingBag,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
  Zap,
  Layers,
  CheckCircle2,
  Plus,
  Minus,
  Sparkles,
  Search,
  Globe,
  Share2,
  ExternalLink,
  Tag
} from 'lucide-react';

interface ProductDetailPageProps {
  productId: string;
  onNavigate: (page: string, productId?: string) => void;
}

export const ProductDetailPage: React.FC<ProductDetailPageProps> = ({ productId, onNavigate }) => {
  const { getProductById, products } = useProducts();
  const { addToQuote } = useQuote();
  const [quantity, setQuantity] = useState(1);
  const [copiedLink, setCopiedLink] = useState(false);

  const product = getProductById(productId);

  // Dynamic SEO updating
  useEffect(() => {
    if (!product) return;

    const originalTitle = document.title;
    const metaTitle = product.seo?.metaTitle || `${product.name} | Saints Tech Solutions Kenya`;
    const metaDesc =
      product.seo?.metaDescription ||
      `${product.name} - ${product.tagline}. High-grade commercial equipment fabricated in Nairobi, Kenya at Juja Road Green Mall.`;
    const metaKeywords =
      product.seo?.keywords ||
      `${product.name}, Saints Tech Solutions, Kenya, Nairobi, commercial vending, water purification`;

    document.title = metaTitle;

    // Update or create meta description
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    descTag.setAttribute('content', metaDesc);

    // Update or create meta keywords
    let keywordsTag = document.querySelector('meta[name="keywords"]');
    if (!keywordsTag) {
      keywordsTag = document.createElement('meta');
      keywordsTag.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsTag);
    }
    keywordsTag.setAttribute('content', metaKeywords);

    // Injected Schema.org Structured Data
    const scriptId = 'product-json-ld';
    let scriptTag = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    const schemaData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: window.location.origin + product.image,
      description: product.description,
      brand: {
        '@type': 'Brand',
        name: 'Saints Tech Solutions'
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'KES',
        price: product.priceKsh,
        availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: 'Saints Tech Solutions'
        }
      }
    };
    scriptTag.text = JSON.stringify(schemaData);

    return () => {
      document.title = originalTitle;
      const script = document.getElementById(scriptId);
      if (script) script.remove();
    };
  }, [product]);

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-bold text-slate-900">Product Not Found</h2>
        <p className="text-slate-600 text-sm">
          The requested equipment or machine does not exist in the current catalog.
        </p>
        <button
          onClick={() => onNavigate('products')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-600 text-white rounded-xl text-xs font-bold"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Products Catalog</span>
        </button>
      </div>
    );
  }

  // Guaranteed 4 related and complementary products
  const sameCategory = products.filter((p) => p.category === product.category && p.id !== product.id);
  const otherProducts = products.filter((p) => p.id !== product.id && p.category !== product.category);
  const relatedProducts = (
    sameCategory.length >= 4
      ? sameCategory.slice(0, 4)
      : [...sameCategory, ...otherProducts].slice(0, 4)
  );

  const handleWhatsAppInquiry = () => {
    const text = `Hello Saints Tech Solutions! I am inquiring about the ${product.name} (KSh ${formatKsh(product.priceKsh)}). I would like to request technical documentation and purchasing terms.`;
    window.open(`https://wa.me/254727072393?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShare = () => {
    const cleanUrl = `${window.location.origin}/product/${product.id}`;
    navigator.clipboard.writeText(cleanUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb={product.name}
        parentPage={{ label: 'Products', page: 'products' }}
        badgeIcon={<ShieldCheck className="w-3.5 h-3.5" />}
        badgeText={`${product.capacity} · Food-Grade SS 304/316 · 1-Year Warranty`}
        badgeColor="emerald"
        title={product.name}
        description={product.tagline}
        backgroundImage={product.image}
        fallbackCategory={product.category}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => onNavigate('products')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Products</span>
            </button>
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>{copiedLink ? 'Link Copied!' : 'Share Page'}</span>
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">

      {/* Main Product Showcase Split */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left: Product Media Gallery */}
        <div className="lg:col-span-6 space-y-4">
          <div className="relative aspect-4/3 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shadow-sm">
            <SafeImage
              src={product.image}
              fallbackCategory={product.category}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-xs px-3 py-1.5 rounded-lg text-xs font-extrabold text-slate-900 shadow-xs">
              {product.capacity}
            </div>
            {product.inStock ? (
              <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-md text-xs font-bold shadow-xs">
                In Stock & Ready for Dispatch
              </div>
            ) : (
              <div className="absolute top-4 right-4 bg-amber-600 text-white px-3 py-1 rounded-md text-xs font-bold shadow-xs">
                Custom Built on Order
              </div>
            )}
          </div>

          {/* Quick Technical Specs Highlights */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
              <Layers className="w-4 h-4 text-sky-600 mx-auto mb-1" />
              <div className="text-[10px] text-slate-400 font-bold uppercase">Material</div>
              <div className="text-xs font-bold text-slate-800 line-clamp-1">{product.material.split(' ')[0]} 304</div>
            </div>
            <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
              <Zap className="w-4 h-4 text-amber-500 mx-auto mb-1" />
              <div className="text-[10px] text-slate-400 font-bold uppercase">Power</div>
              <div className="text-xs font-bold text-slate-800 line-clamp-1">{product.power.split('/')[0]}</div>
            </div>
            <div className="p-3 bg-white rounded-xl border border-slate-200 text-center">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
              <div className="text-[10px] text-slate-400 font-bold uppercase">Warranty</div>
              <div className="text-xs font-bold text-slate-800">12 Months</div>
            </div>
          </div>
        </div>

        {/* Right: Product Commercial Information & Order Controls */}
        <div className="lg:col-span-6 space-y-6">
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
              {product.category.replace('-', ' ')}
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
              {product.name}
            </h1>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              {product.tagline}
            </p>
          </div>

          {/* Pricing Box */}
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <div className="text-xs text-slate-500 font-medium">Ex-Factory Nairobi Price</div>
              <div className="flex items-baseline gap-3 mt-0.5">
                <span className="text-3xl font-extrabold text-slate-900 tabular-nums">
                  KSh {formatKsh(product.priceKsh)}
                </span>
                {product.originalPriceKsh && (
                  <span className="text-sm text-slate-400 line-through tabular-nums">
                    KSh {formatKsh(product.originalPriceKsh)}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right text-xs text-slate-500">
              <span className="block font-semibold text-emerald-700">Free Workshop Testing</span>
              <span>Juja Road Green Mall, Nairobi</span>
            </div>
          </div>

          {/* Quantity & Order Actions */}
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-slate-300 rounded-xl p-1 bg-white shadow-2xs">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 cursor-pointer"
                  title="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center text-sm font-bold tabular-nums">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-2 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 cursor-pointer"
                  title="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={() => addToQuote(product, quantity)}
                className="flex-1 py-3 px-5 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-xs sm:text-sm font-bold rounded-xl shadow-md transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>Add to Quote Basket ({quantity})</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <button
                onClick={handleWhatsAppInquiry}
                className="py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-2 cursor-pointer shadow-xs"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Inquire on WhatsApp</span>
              </button>
              <a
                href="tel:+254727072393"
                className="py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <PhoneCall className="w-4 h-4 text-emerald-400" />
                <span className="tabular-nums">+254 727 072 393</span>
              </a>
            </div>
          </div>

          {/* Quick Assurance Badges */}
          <div className="border-t border-slate-200 pt-4 space-y-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Includes complete on-site installation & staff operational training</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Full warranty with genuine replacement spare parts permanently in stock</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Countrywide delivery across all 47 counties of Kenya</span>
            </div>
          </div>
        </div>
      </div>

      {/* Comprehensive Technical Documentation & Specs Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-4 border-t border-slate-200">
        {/* Left Column: Full Description & Features */}
        <div className="lg:col-span-7 space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-slate-900">
              Equipment Overview & Applications
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {product.description}
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-900">
              Key Engineering Features
            </h3>
            <div className="grid grid-cols-1 gap-2.5">
              {(product.features || []).map((feat, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                  <span className="text-xs text-slate-700 leading-relaxed font-medium">{feat}</span>
                </div>
              ))}
            </div>
          </div>

          {/* SEO Meta Card Inspector for this product */}
          {product.seo && (
            <div className="bg-sky-50/70 border border-sky-200/80 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sky-800 text-xs font-bold uppercase tracking-wider">
                  <Globe className="w-4 h-4" />
                  <span>Configured SEO Metadata (Live Search Indexing)</span>
                </div>
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                  Search Optimized
                </span>
              </div>

              {/* Google Search Result Preview Card */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-1">
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                  <span>https://saintstech.co.ke</span>
                  <span>›</span>
                  <span className="text-slate-700">products</span>
                  <span>›</span>
                  <span className="text-slate-900">{product.id}</span>
                </div>
                <div className="text-sm sm:text-base font-semibold text-blue-700 hover:underline cursor-pointer">
                  {product.seo.metaTitle || product.name}
                </div>
                <p className="text-xs text-slate-600 leading-snug line-clamp-2">
                  {product.seo.metaDescription || product.tagline}
                </p>
              </div>

              <div className="space-y-2 text-xs">
                {product.seo.keywords && (
                  <div>
                    <span className="font-semibold text-slate-700">Target Keywords: </span>
                    <span className="text-slate-600 italic">{product.seo.keywords}</span>
                  </div>
                )}
                {product.seo.tags && product.seo.tags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="font-semibold text-slate-700 mr-1 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Tags:
                    </span>
                    {(product.seo.tags || []).map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-[11px] text-slate-700 font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Full Specifications Table */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3">
              Technical Specifications
            </h3>

            <div className="divide-y divide-slate-100 text-xs">
              <div className="py-2.5 flex justify-between">
                <span className="text-slate-500 font-medium">Model Code</span>
                <span className="text-slate-900 font-bold font-mono">{product.id.toUpperCase()}</span>
              </div>
              <div className="py-2.5 flex justify-between">
                <span className="text-slate-500 font-medium">Capacity</span>
                <span className="text-slate-900 font-semibold text-right">{product.capacity}</span>
              </div>
              <div className="py-2.5 flex justify-between">
                <span className="text-slate-500 font-medium">Material Grade</span>
                <span className="text-slate-900 font-semibold text-right">{product.material}</span>
              </div>
              <div className="py-2.5 flex justify-between">
                <span className="text-slate-500 font-medium">Electrical Supply</span>
                <span className="text-slate-900 font-semibold text-right">{product.power}</span>
              </div>
              {(product.specs || []).map((sp, idx) => (
                <div key={idx} className="py-2.5 flex justify-between">
                  <span className="text-slate-500 font-medium">{sp.label}</span>
                  <span className="text-slate-900 font-semibold text-right tabular-nums">{sp.value}</span>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <button
                onClick={() => onNavigate('contact')}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors cursor-pointer text-center"
              >
                Request Custom Engineering Modifications
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Related / Alternative Machinery Section at the bottom */}
      <div className="pt-10 border-t border-slate-200 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-sky-600 mb-1">
              Complementary & Alternative Systems
            </div>
            <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Related Equipment You May Also Need
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Pair your {product.name} with compatible pasteurizers, water stations, or genuine spare parts.
            </p>
          </div>
          <button
            onClick={() => onNavigate('products')}
            className="text-xs font-bold text-sky-700 hover:text-sky-800 flex items-center gap-1.5 self-start sm:self-auto shrink-0 cursor-pointer"
          >
            <span>Explore All Equipment ({products.length})</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* 4-Column Related Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(relatedProducts || []).map((rel) => (
            <div
              key={rel.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between group"
            >
              <div>
                {/* Photo container */}
                <div
                  onClick={() => onNavigate('product-detail', rel.id)}
                  className="relative aspect-4/3 rounded-t-2xl overflow-hidden bg-slate-100 cursor-pointer"
                >
                  <SafeImage
                    src={rel.image}
                    fallbackCategory={rel.category}
                    alt={rel.name}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                  />
                  <div className="absolute top-2.5 left-2.5 bg-white/95 backdrop-blur-2xs px-2.5 py-0.5 rounded text-[10px] font-bold text-slate-800 shadow-2xs">
                    {rel.capacity}
                  </div>
                  {rel.inStock && (
                    <div className="absolute top-2.5 right-2.5 bg-emerald-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                      In Stock
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-4 space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">
                    {rel.category.replace('-', ' ')}
                  </div>
                  <a
                    href={`/product/${rel.id}`}
                    onClick={(e) => { e.preventDefault(); onNavigate('product-detail', rel.id); }}
                    className="text-xs font-bold text-slate-900 line-clamp-2 leading-snug hover:text-sky-700 transition-colors cursor-pointer block"
                  >
                    {rel.name}
                  </a>
                  <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                    {rel.tagline}
                  </p>

                  <div className="pt-1 flex items-baseline gap-2">
                    <span className="text-base font-extrabold text-slate-900 tabular-nums">
                      KSh {formatKsh(rel.priceKsh)}
                    </span>
                    {rel.originalPriceKsh && (
                      <span className="text-[11px] text-slate-400 line-through tabular-nums">
                        KSh {formatKsh(rel.originalPriceKsh)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Quick Action buttons */}
              <div className="p-4 pt-0 border-t border-slate-100 flex items-center gap-1.5 mt-2">
                <a
                  href={`/product/${rel.id}`}
                  onClick={(e) => { e.preventDefault(); onNavigate('product-detail', rel.id); }}
                  className="flex-1 py-1.5 px-2.5 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer text-center block"
                >
                  View Page
                </a>
                <button
                  onClick={() => addToQuote(rel, 1)}
                  className="py-1.5 px-2.5 text-[11px] font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                  title="Add to quotation basket"
                >
                  <ShoppingBag className="w-3 h-3" />
                  <span>Quote</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Engineering Consultation Banner at bottom */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1 max-w-xl">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-400">
              Need a Custom Combination Setup?
            </span>
            <h4 className="text-lg font-bold">
              Looking to combine Milk ATMs with Pasteurizers or RO Skids?
            </h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              We package complete milk processing and water bottling plants with integrated piping, power backups, and KeBS certification testing at discounted turnkey bundle pricing.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <a
              href="https://wa.me/254727072393?text=Hello%20Saints%20Tech%20Solutions!%20I%20am%20interested%20in%20a%20turnkey%20combination%20package."
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp Us: +254 727 072 393</span>
            </a>
            <button
              onClick={() => onNavigate('contact')}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl border border-slate-700 transition-colors cursor-pointer"
            >
              Visit Juja Road Office
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};
